import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);

const CEFR_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CATS = ['vocab', 'grammar', 'reading', 'expressions'];
const QUESTIONS_PER_CAT_PER_LEVEL = 4;
const QUESTIONS_PER_LEVEL = CATS.length * QUESTIONS_PER_CAT_PER_LEVEL; // 16
const TOTAL_QUESTIONS = CEFR_ORDER.length * QUESTIONS_PER_LEVEL; // 96

// ── STRINGS (UI chrome only — never the Spanish being tested) ────────────────
const S = {
  en: {
    title: 'Spanish Level Test',
    subtitle: `Find your CEFR level in ${TOTAL_QUESTIONS} questions`,
    contentNote: 'A1–A2 questions appear in your interface language. From B1 onward, everything is in Spanish only — just like the real thing.',
    start: 'Start test',
    question: 'Question',
    of: 'of',
    result: 'Your result',
    score: 'Score',
    level: 'CEFR Level',
    breakdown: 'Accuracy by category',
    ladder: 'CEFR level ladder',
    ladderSub: 'How you performed at each level',
    recommendations: 'Tips to improve',
    saveResult: 'Save result',
    saved: '✅ Result saved',
    saving: 'Saving...',
    back: '← Portal',
    history: 'Previous tests',
    nextAvailable: 'Next test available from',
    alreadyTook: 'You already took the test this quarter.',
    viewResult: 'View your result',
    categories: { vocab: 'Vocabulary', grammar: 'Grammar', reading: 'Reading', expressions: 'Expressions' },
    status: { excellent: 'Excellent', good: 'Good', weak: 'Needs work' },
    review: 'Full answer review',
    reviewSub: 'Every question, your answer, the correct answer, and why',
    showWrongOnly: 'Show mistakes only',
    showAll: 'Show all questions',
    yourAnswer: 'Your answer',
    correctAnswer: 'Correct answer',
    correctTag: 'Correct',
    wrongTag: 'Incorrect',
    noMistakes: '🎉 No mistakes in this category — great job!',
    levels: {
      A1: { label: 'A1', sublabel: 'Beginner', color: '#16a34a', bg: '#dcfce7', desc: 'You understand and use very basic everyday expressions. Focus on core vocabulary and simple present-tense sentences.' },
      A2: { label: 'A2', sublabel: 'Elementary', color: '#22b8a0', bg: '#d0f5ee', desc: 'You can handle simple, routine exchanges. Keep building everyday vocabulary and start using the past tense confidently.' },
      B1: { label: 'B1', sublabel: 'Intermediate', color: '#0284c7', bg: '#dbeafe', desc: 'You can deal with most situations while travelling or at work. Focus on connecting ideas and using the subjunctive in simple contexts.' },
      B2: { label: 'B2', sublabel: 'Upper Intermediate', color: '#4f46e5', bg: '#e0e7ff', desc: 'You can interact with fluency and spontaneity. Work on nuanced grammar (conditional, subjunctive) and more formal vocabulary.' },
      C1: { label: 'C1', sublabel: 'Advanced', color: '#7c3aed', bg: '#ede9fe', desc: 'You express yourself fluently on complex topics. Focus on idiomatic expressions, register, and precision in writing.' },
      C2: { label: 'C2', sublabel: 'Proficiency', color: '#b45309', bg: '#fef3c7', desc: 'You have near-native mastery. Keep refining subtlety, rhetorical nuance, and specialised or literary vocabulary.' },
    },
    recs: {
      vocab: 'Focus on expanding vocabulary — try saving 5 new words per class in your notebook.',
      grammar: 'Practise grammar structures — the Complete the Sentence game is great for this.',
      reading: 'Read more — start with short articles or graded readers in Spanish.',
      expressions: 'Study idioms and set phrases — check the Modismos game in Practise.',
    },
    perfect: 'Excellent! You aced it.',
    retakeInfo: 'You can take this test every 3 months to track your real progress.',
  },
  pt: {
    title: 'Teste de Nível de Espanhol',
    subtitle: `Descubra seu nível no QECR em ${TOTAL_QUESTIONS} perguntas`,
    contentNote: 'As perguntas de A1–A2 aparecem no idioma da sua interface. A partir do B1, tudo é em espanhol — assim como no teste real.',
    start: 'Iniciar teste',
    question: 'Pergunta',
    of: 'de',
    result: 'Seu resultado',
    score: 'Pontuação',
    level: 'Nível QECR',
    breakdown: 'Precisão por categoria',
    ladder: 'Escala de níveis do QECR',
    ladderSub: 'Seu desempenho em cada nível',
    recommendations: 'Dicas para melhorar',
    saveResult: 'Salvar resultado',
    saved: '✅ Resultado salvo',
    saving: 'Salvando...',
    back: '← Portal',
    history: 'Testes anteriores',
    nextAvailable: 'Próximo teste disponível a partir de',
    alreadyTook: 'Você já fez o teste este trimestre.',
    viewResult: 'Ver seu resultado',
    categories: { vocab: 'Vocabulário', grammar: 'Gramática', reading: 'Leitura', expressions: 'Expressões' },
    status: { excellent: 'Excelente', good: 'Bom', weak: 'A reforçar' },
    review: 'Revisão completa das respostas',
    reviewSub: 'Cada pergunta, sua resposta, a resposta correta e o porquê',
    showWrongOnly: 'Mostrar só os erros',
    showAll: 'Mostrar todas as perguntas',
    yourAnswer: 'Sua resposta',
    correctAnswer: 'Resposta correta',
    correctTag: 'Correta',
    wrongTag: 'Incorreta',
    noMistakes: '🎉 Nenhum erro nesta categoria — muito bem!',
    levels: {
      A1: { label: 'A1', sublabel: 'Iniciante', color: '#16a34a', bg: '#dcfce7', desc: 'Você entende e usa expressões cotidianas muito básicas. Foque em vocabulário essencial e frases simples no presente.' },
      A2: { label: 'A2', sublabel: 'Elementar', color: '#22b8a0', bg: '#d0f5ee', desc: 'Você lida com trocas simples e rotineiras. Continue ampliando o vocabulário e comece a usar o passado com confiança.' },
      B1: { label: 'B1', sublabel: 'Intermediário', color: '#0284c7', bg: '#dbeafe', desc: 'Você lida com a maioria das situações de viagem ou trabalho. Foque em conectar ideias e usar o subjuntivo em contextos simples.' },
      B2: { label: 'B2', sublabel: 'Intermediário Alto', color: '#4f46e5', bg: '#e0e7ff', desc: 'Você interage com fluência e espontaneidade. Trabalhe nuances gramaticais (condicional, subjuntivo) e vocabulário mais formal.' },
      C1: { label: 'C1', sublabel: 'Avançado', color: '#7c3aed', bg: '#ede9fe', desc: 'Você se expressa com fluência sobre temas complexos. Foque em expressões idiomáticas, registro e precisão na escrita.' },
      C2: { label: 'C2', sublabel: 'Proficiência', color: '#b45309', bg: '#fef3c7', desc: 'Você tem domínio quase nativo. Continue refinando sutilezas, nuances retóricas e vocabulário especializado ou literário.' },
    },
    recs: {
      vocab: 'Foque em ampliar o vocabulário — tente salvar 5 palavras novas por aula na sua caderneta.',
      grammar: 'Pratique estruturas gramaticais — o jogo Completa a Frase é ótimo para isso.',
      reading: 'Leia mais — comece com artigos curtos ou leituras graduadas em espanhol.',
      expressions: 'Estude expressões e modismos — veja o jogo de Modismos na seção Praticar.',
    },
    perfect: 'Excelente! Você acertou tudo.',
    retakeInfo: 'Você pode fazer este teste a cada 3 meses para acompanhar seu progresso real.',
  },
  es: {
    title: 'Test de Nivel de Español',
    subtitle: `Descubre tu nivel MCER en ${TOTAL_QUESTIONS} preguntas`,
    contentNote: 'Las preguntas de A1–A2 aparecen en tu idioma de interfaz. A partir de B1, todo está en español — igual que en la vida real.',
    start: 'Iniciar test',
    question: 'Pregunta',
    of: 'de',
    result: 'Tu resultado',
    score: 'Puntuación',
    level: 'Nivel MCER',
    breakdown: 'Precisión por categoría',
    ladder: 'Escala de niveles MCER',
    ladderSub: 'Tu desempeño en cada nivel',
    recommendations: 'Consejos para mejorar',
    saveResult: 'Guardar resultado',
    saved: '✅ Resultado guardado',
    saving: 'Guardando...',
    back: '← Portal',
    history: 'Tests anteriores',
    nextAvailable: 'Próximo test disponible desde el',
    alreadyTook: 'Ya hiciste el test este trimestre.',
    viewResult: 'Ver tu resultado',
    categories: { vocab: 'Vocabulario', grammar: 'Gramática', reading: 'Lectura', expressions: 'Expresiones' },
    status: { excellent: 'Excelente', good: 'Bueno', weak: 'A reforzar' },
    review: 'Revisión completa de respuestas',
    reviewSub: 'Cada pregunta, tu respuesta, la respuesta correcta y el porqué',
    showWrongOnly: 'Mostrar solo errores',
    showAll: 'Mostrar todas las preguntas',
    yourAnswer: 'Tu respuesta',
    correctAnswer: 'Respuesta correcta',
    correctTag: 'Correcta',
    wrongTag: 'Incorrecta',
    noMistakes: '🎉 Sin errores en esta categoría — ¡muy bien!',
    levels: {
      A1: { label: 'A1', sublabel: 'Principiante', color: '#16a34a', bg: '#dcfce7', desc: 'Entiendes y usas expresiones cotidianas muy básicas. Enfócate en vocabulario esencial y frases simples en presente.' },
      A2: { label: 'A2', sublabel: 'Elemental', color: '#22b8a0', bg: '#d0f5ee', desc: 'Manejas intercambios simples y rutinarios. Sigue ampliando vocabulario y empieza a usar el pasado con confianza.' },
      B1: { label: 'B1', sublabel: 'Intermedio', color: '#0284c7', bg: '#dbeafe', desc: 'Te manejas en la mayoría de situaciones de viaje o trabajo. Enfócate en conectar ideas y usar el subjuntivo en contextos simples.' },
      B2: { label: 'B2', sublabel: 'Intermedio Alto', color: '#4f46e5', bg: '#e0e7ff', desc: 'Interactúas con fluidez y espontaneidad. Trabaja matices gramaticales (condicional, subjuntivo) y vocabulario más formal.' },
      C1: { label: 'C1', sublabel: 'Avanzado', color: '#7c3aed', bg: '#ede9fe', desc: 'Te expresas con fluidez sobre temas complejos. Enfócate en modismos, registro y precisión al escribir.' },
      C2: { label: 'C2', sublabel: 'Maestría', color: '#b45309', bg: '#fef3c7', desc: 'Tienes un dominio casi nativo. Sigue puliendo matices, sutilezas retóricas y vocabulario especializado o literario.' },
    },
    recs: {
      vocab: 'Enfócate en ampliar vocabulario — intenta guardar 5 palabras nuevas por clase en tu libreta.',
      grammar: 'Practica estructuras gramaticales — el juego Completa la Frase es ideal para esto.',
      reading: 'Lee más — empieza con artículos cortos o lecturas graduadas en español.',
      expressions: 'Estudia modismos y frases hechas — revisa el juego de Modismos en Practicar.',
    },
    perfect: '¡Excelente! Lo hiciste perfecto.',
    retakeInfo: 'Puedes hacer este test cada 3 meses para seguir tu progreso real.',
  },
};

// ── QUESTION BANK ─────────────────────────────────────────────────────────────
// A1–A2: translated (q/opts/expl are {en, pt, es} objects) so beginners can
//        read the question in their own interface language.
// B1–C2: Spanish only (q/opts/expl are plain strings/arrays) — no translation,
//        matching real-world CEFR testing from intermediate level up.
let _id = 0;
const nid = () => ++_id;

const QUESTIONS = [
  // ══════════════════════════ A1 ══════════════════════════
  { id: nid(), cefr: 'A1', cat: 'vocab', translated: true,
    q: { en: 'What does "buenos días" mean?', pt: 'O que significa "buenos días"?', es: '¿Qué significa "buenos días"?' },
    opts: { en: ['Good morning', 'Good night', 'See you later', 'Thank you'], pt: ['Bom dia', 'Boa noite', 'Até logo', 'Obrigado'], es: ['Buen día (saludo matutino)', 'Buenas noches', 'Hasta luego', 'Gracias'] },
    answer: 0, expl: { en: 'A morning greeting.', pt: 'Uma saudação usada pela manhã.', es: 'Saludo usado por la mañana.' } },
  { id: nid(), cefr: 'A1', cat: 'vocab', translated: true,
    q: { en: 'What does "gracias" mean?', pt: 'O que significa "gracias"?', es: '¿Qué significa "gracias"?' },
    opts: { en: ['Please', 'Thank you', 'Sorry', 'Goodbye'], pt: ['Por favor', 'Obrigado', 'Desculpa', 'Adeus'], es: ['Por favor', 'Gracias (agradecimiento)', 'Perdón', 'Adiós'] },
    answer: 1, expl: { en: 'Used to say thank you.', pt: 'Usado para agradecer.', es: 'Se usa para agradecer.' } },
  { id: nid(), cefr: 'A1', cat: 'vocab', translated: true,
    q: { en: 'What does "por favor" mean?', pt: 'O que significa "por favor"?', es: '¿Qué significa "por favor"?' },
    opts: { en: ['Please', 'Thank you', 'Sorry', "You're welcome"], pt: ['Por favor', 'Obrigado', 'Desculpa', 'De nada'], es: ['Por favor (petición cortés)', 'Gracias', 'Perdón', 'De nada'] },
    answer: 0, expl: { en: 'Used to ask for something politely.', pt: 'Usado para pedir algo com cortesia.', es: 'Se usa para pedir algo con cortesía.' } },
  { id: nid(), cefr: 'A1', cat: 'vocab', translated: true,
    q: { en: 'What does "buenas noches" mean?', pt: 'O que significa "buenas noches"?', es: '¿Qué significa "buenas noches"?' },
    opts: { en: ['Good afternoon', 'Good night', 'Good morning', 'See you later'], pt: ['Boa tarde', 'Boa noite', 'Bom dia', 'Até logo'], es: ['Buenas tardes', 'Buenas noches (saludo o despedida)', 'Buenos días', 'Hasta luego'] },
    answer: 1, expl: { en: 'An evening greeting or farewell.', pt: 'Saudação ou despedida noturna.', es: 'Saludo o despedida nocturna.' } },

  { id: nid(), cefr: 'A1', cat: 'grammar', translated: true,
    q: { en: 'Choose the correct verb: "Yo ___ estudiante."', pt: 'Escolha o verbo correto: "Yo ___ estudiante."', es: 'Elige el verbo correcto: "Yo ___ estudiante."' },
    opts: { en: ['soy', 'estás', 'es', 'somos'], pt: ['soy', 'estás', 'es', 'somos'], es: ['soy', 'estás', 'es', 'somos'] },
    answer: 0, expl: { en: '"Yo" is conjugated with "soy".', pt: '"Yo" se conjuga com "soy".', es: '"Yo" se conjuga con "soy".' } },
  { id: nid(), cefr: 'A1', cat: 'grammar', translated: true,
    q: { en: 'Complete: "Ella ___ profesora."', pt: 'Complete: "Ella ___ profesora."', es: 'Completa: "Ella ___ profesora."' },
    opts: { en: ['soy', 'es', 'eres', 'son'], pt: ['soy', 'es', 'eres', 'son'], es: ['soy', 'es', 'eres', 'son'] },
    answer: 1, expl: { en: '"Ella" + ser = "es".', pt: '"Ella" + ser = "es".', es: '"Ella" + ser = "es".' } },
  { id: nid(), cefr: 'A1', cat: 'grammar', translated: true,
    q: { en: 'Complete: "Nosotros ___ (ser) amigos."', pt: 'Complete: "Nosotros ___ (ser) amigos."', es: 'Completa: "Nosotros ___ (ser) amigos."' },
    opts: { en: ['somos', 'eres', 'es', 'soy'], pt: ['somos', 'eres', 'es', 'soy'], es: ['somos', 'eres', 'es', 'soy'] },
    answer: 0, expl: { en: '"Nosotros" is conjugated with "somos".', pt: '"Nosotros" se conjuga com "somos".', es: '"Nosotros" se conjuga con "somos".' } },
  { id: nid(), cefr: 'A1', cat: 'grammar', translated: true,
    q: { en: 'Complete: "Tú ___ (tener) un libro."', pt: 'Complete: "Tú ___ (tener) un libro."', es: 'Completa: "Tú ___ (tener) un libro."' },
    opts: { en: ['tengo', 'tienes', 'tiene', 'tenemos'], pt: ['tengo', 'tienes', 'tiene', 'tenemos'], es: ['tengo', 'tienes', 'tiene', 'tenemos'] },
    answer: 1, expl: { en: '"Tú" + tener = "tienes".', pt: '"Tú" + tener = "tienes".', es: '"Tú" + tener = "tienes".' } },

  { id: nid(), cefr: 'A1', cat: 'reading', translated: true,
    q: { en: 'Read: "Me llamo Ana. Tengo 20 años y vivo en Madrid." — How old is Ana?', pt: 'Leia: "Me llamo Ana. Tengo 20 años y vivo en Madrid." — Quantos anos tem Ana?', es: 'Lee: "Me llamo Ana. Tengo 20 años y vivo en Madrid." — ¿Cuántos años tiene Ana?' },
    opts: { en: ['15', '20', '25', '30'], pt: ['15', '20', '25', '30'], es: ['15', '20', '25', '30'] },
    answer: 1, expl: { en: 'The text says "Tengo 20 años".', pt: 'O texto diz "Tengo 20 años".', es: 'El texto dice "Tengo 20 años".' } },
  { id: nid(), cefr: 'A1', cat: 'reading', translated: true,
    q: { en: 'Read: "Juan tiene un perro y un gato. Le gustan mucho los animales." — What pets does Juan have?', pt: 'Leia: "Juan tiene un perro y un gato. Le gustan mucho los animales." — Que animais de estimação Juan tem?', es: 'Lee: "Juan tiene un perro y un gato. Le gustan mucho los animales." — ¿Qué mascotas tiene Juan?' },
    opts: { en: ['Only a dog', 'A dog and a cat', 'Only a cat', 'A bird'], pt: ['Só um cachorro', 'Um cachorro e um gato', 'Só um gato', 'Um pássaro'], es: ['Solo un perro', 'Un perro y un gato', 'Solo un gato', 'Un pájaro'] },
    answer: 1, expl: { en: 'The text mentions both animals.', pt: 'O texto menciona os dois animais.', es: 'El texto menciona ambos animales.' } },
  { id: nid(), cefr: 'A1', cat: 'reading', translated: true,
    q: { en: 'Read: "Pedro vive con su familia. Su casa tiene tres habitaciones." — How many rooms does the house have?', pt: 'Leia: "Pedro vive con su familia. Su casa tiene tres habitaciones." — Quantos quartos tem a casa?', es: 'Lee: "Pedro vive con su familia. Su casa tiene tres habitaciones." — ¿Cuántas habitaciones tiene la casa?' },
    opts: { en: ['2', '3', '4', '5'], pt: ['2', '3', '4', '5'], es: ['2', '3', '4', '5'] },
    answer: 1, expl: { en: 'The text says "tres habitaciones".', pt: 'O texto diz "tres habitaciones".', es: 'El texto dice "tres habitaciones".' } },
  { id: nid(), cefr: 'A1', cat: 'reading', translated: true,
    q: { en: 'Read: "Los lunes, Sara va a la escuela a las 8 de la mañana." — What day does the text mention?', pt: 'Leia: "Los lunes, Sara va a la escuela a las 8 de la mañana." — Que dia o texto menciona?', es: 'Lee: "Los lunes, Sara va a la escuela a las 8 de la mañana." — ¿Qué día menciona el texto?' },
    opts: { en: ['Sunday', 'Monday', 'Friday', 'Saturday'], pt: ['Domingo', 'Segunda-feira', 'Sexta-feira', 'Sábado'], es: ['Domingo', 'Lunes', 'Viernes', 'Sábado'] },
    answer: 1, expl: { en: 'The text mentions "los lunes".', pt: 'O texto menciona "los lunes".', es: 'El texto menciona "los lunes".' } },

  { id: nid(), cefr: 'A1', cat: 'expressions', translated: true,
    q: { en: 'What does "¿Qué tal?" mean?', pt: 'O que significa "¿Qué tal?"?', es: '¿Qué significa "¿Qué tal?"?' },
    opts: { en: ["What's your name?", 'How are you?', 'Where are you from?', 'What time is it?'], pt: ['Qual é o seu nome?', 'Como você está?', 'De onde você é?', 'Que horas são?'], es: ['¿Cómo te llamas?', '¿Cómo estás?', '¿De dónde eres?', '¿Qué hora es?'] },
    answer: 1, expl: { en: 'Used to ask how someone is doing.', pt: 'Usado para perguntar como alguém está.', es: 'Se usa para preguntar cómo está alguien.' } },
  { id: nid(), cefr: 'A1', cat: 'expressions', translated: true,
    q: { en: 'What does "de nada" mean?', pt: 'O que significa "de nada"?', es: '¿Qué significa "de nada"?' },
    opts: { en: ["You're welcome", 'Good night', 'Excuse me', 'See you soon'], pt: ['De nada / não há de quê', 'Boa noite', 'Com licença', 'Até logo'], es: ['De nada (respuesta a "gracias")', 'Buenas noches', 'Perdón', 'Hasta pronto'] },
    answer: 0, expl: { en: 'The usual reply to "gracias".', pt: 'Resposta habitual a "gracias".', es: 'Respuesta habitual a "gracias".' } },
  { id: nid(), cefr: 'A1', cat: 'expressions', translated: true,
    q: { en: 'What does "mucho gusto" mean?', pt: 'O que significa "mucho gusto"?', es: '¿Qué significa "mucho gusto"?' },
    opts: { en: ['Nice to meet you', 'Good luck', 'See you soon', 'No problem'], pt: ['Muito prazer', 'Boa sorte', 'Até logo', 'Sem problema'], es: ['Encantado de conocerte', 'Buena suerte', 'Hasta pronto', 'No hay problema'] },
    answer: 0, expl: { en: 'Used when meeting someone for the first time.', pt: 'Usado ao conhecer alguém.', es: 'Se usa al conocer a alguien.' } },
  { id: nid(), cefr: 'A1', cat: 'expressions', translated: true,
    q: { en: 'What does "hasta luego" mean?', pt: 'O que significa "hasta luego"?', es: '¿Qué significa "hasta luego"?' },
    opts: { en: ['Good morning', 'See you later', 'Excuse me', 'Thank you'], pt: ['Bom dia', 'Até logo', 'Com licença', 'Obrigado'], es: ['Buenos días', 'Hasta luego (despedida)', 'Perdón', 'Gracias'] },
    answer: 1, expl: { en: 'A common farewell expression.', pt: 'Expressão comum de despedida.', es: 'Expresión de despedida.' } },

  // ══════════════════════════ A2 ══════════════════════════
  { id: nid(), cefr: 'A2', cat: 'vocab', translated: true,
    q: { en: 'What does "todos los días" mean?', pt: 'O que significa "todos los días"?', es: '¿Qué significa "todos los días"?' },
    opts: { en: ['Every day', 'Some days', 'Never', 'Once a week'], pt: ['Todos os dias', 'Alguns dias', 'Nunca', 'Uma vez por semana'], es: ['Cada día', 'Algunos días', 'Nunca', 'Una vez por semana'] },
    answer: 0, expl: { en: 'Indicates daily frequency.', pt: 'Indica frequência diária.', es: 'Indica frecuencia diaria.' } },
  { id: nid(), cefr: 'A2', cat: 'vocab', translated: true,
    q: { en: 'What does "hacer la compra" mean?', pt: 'O que significa "hacer la compra"?', es: '¿Qué significa "hacer la compra"?' },
    opts: { en: ['To do homework', 'To go grocery shopping', 'To make a decision', 'To do a favor'], pt: ['Fazer a lição de casa', 'Fazer compras no mercado', 'Tomar uma decisão', 'Fazer um favor'], es: ['Hacer los deberes', 'Ir de compras al supermercado', 'Tomar una decisión', 'Hacer un favor'] },
    answer: 1, expl: { en: 'Refers to buying groceries.', pt: 'Refere-se a comprar mantimentos.', es: 'Se refiere a comprar comida.' } },
  { id: nid(), cefr: 'A2', cat: 'vocab', translated: true,
    q: { en: 'What does "dar un paseo" mean?', pt: 'O que significa "dar un paseo"?', es: '¿Qué significa "dar un paseo"?' },
    opts: { en: ['To take a walk', 'To give a gift', 'To drive a car', 'To take a nap'], pt: ['Dar uma caminhada', 'Dar um presente', 'Dirigir um carro', 'Tirar uma soneca'], es: ['Caminar por placer', 'Regalar algo', 'Conducir un coche', 'Dormir una siesta'] },
    answer: 0, expl: { en: '"Dar un paseo" = to walk for leisure.', pt: '"Dar un paseo" = caminhar por lazer.', es: '"Dar un paseo" = caminar por placer.' } },
  { id: nid(), cefr: 'A2', cat: 'vocab', translated: true,
    q: { en: 'What does "estar resfriado" mean?', pt: 'O que significa "estar resfriado"?', es: '¿Qué significa "estar resfriado"?' },
    opts: { en: ['To have a cold', 'To be cold (temperature)', 'To be scared', 'To be tired'], pt: ['Estar gripado/resfriado', 'Estar com frio (temperatura)', 'Estar com medo', 'Estar cansado'], es: ['Tener un resfriado', 'Tener frío (temperatura)', 'Tener miedo', 'Estar cansado'] },
    answer: 0, expl: { en: 'This refers to a cold, not the weather.', pt: 'Refere-se a um resfriado, não ao clima.', es: 'Se refiere a un resfriado, no al clima.' } },

  { id: nid(), cefr: 'A2', cat: 'grammar', translated: true,
    q: { en: 'Complete: "Ayer yo ___ (ir) al cine."', pt: 'Complete: "Ayer yo ___ (ir) al cine."', es: 'Completa: "Ayer yo ___ (ir) al cine."' },
    opts: { en: ['voy', 'fui', 'iré', 'iba'], pt: ['voy', 'fui', 'iré', 'iba'], es: ['voy', 'fui', 'iré', 'iba'] },
    answer: 1, expl: { en: 'Preterite of "ir" for the past.', pt: 'Pretérito de "ir" para o passado.', es: 'Pretérito de "ir" para el pasado.' } },
  { id: nid(), cefr: 'A2', cat: 'grammar', translated: true,
    q: { en: 'Complete: "Ella siempre ___ (levantarse) temprano."', pt: 'Complete: "Ella siempre ___ (levantarse) temprano."', es: 'Completa: "Ella siempre ___ (levantarse) temprano."' },
    opts: { en: ['se levanta', 'se levantó', 'se levantará', 'levantándose'], pt: ['se levanta', 'se levantó', 'se levantará', 'levantándose'], es: ['se levanta', 'se levantó', 'se levantará', 'levantándose'] },
    answer: 0, expl: { en: 'Present tense for habits.', pt: 'Presente para hábitos.', es: 'Presente para hábitos.' } },
  { id: nid(), cefr: 'A2', cat: 'grammar', translated: true,
    q: { en: 'Complete: "Mañana nosotros ___ (viajar) a Perú."', pt: 'Complete: "Mañana nosotros ___ (viajar) a Perú."', es: 'Completa: "Mañana nosotros ___ (viajar) a Perú."' },
    opts: { en: ['viajamos', 'viajaremos', 'viajábamos', 'viajaríamos'], pt: ['viajamos', 'viajaremos', 'viajábamos', 'viajaríamos'], es: ['viajamos', 'viajaremos', 'viajábamos', 'viajaríamos'] },
    answer: 1, expl: { en: 'Simple future for plans.', pt: 'Futuro simples para planos.', es: 'Futuro simple para planes.' } },
  { id: nid(), cefr: 'A2', cat: 'grammar', translated: true,
    q: { en: 'Complete: "Ella ___ (estudiar) español desde hace dos años."', pt: 'Complete: "Ella ___ (estudiar) español desde hace dos años."', es: 'Completa: "Ella ___ (estudiar) español desde hace dos años."' },
    opts: { en: ['estudia', 'estudió', 'estudiará', 'estudiando'], pt: ['estudia', 'estudió', 'estudiará', 'estudiando'], es: ['estudia', 'estudió', 'estudiará', 'estudiando'] },
    answer: 0, expl: { en: 'Present tense for ongoing actions.', pt: 'Presente para ações que continuam.', es: 'Presente para acciones que continúan.' } },

  { id: nid(), cefr: 'A2', cat: 'reading', translated: true,
    q: { en: 'Read: "Todos los sábados, Marta va al mercado con su madre. Compran fruta y verdura fresca." — What does Marta do on Saturdays?', pt: 'Leia: "Todos los sábados, Marta va al mercado con su madre. Compran fruta y verdura fresca." — O que Marta faz aos sábados?', es: 'Lee: "Todos los sábados, Marta va al mercado con su madre. Compran fruta y verdura fresca." — ¿Qué hace Marta los sábados?' },
    opts: { en: ['She studies', 'She goes to the market with her mother', 'She works', 'She travels'], pt: ['Ela estuda', 'Ela vai ao mercado com a mãe', 'Ela trabalha', 'Ela viaja'], es: ['Estudia', 'Va al mercado con su madre', 'Trabaja', 'Viaja'] },
    answer: 1, expl: { en: 'The text states it directly.', pt: 'O texto afirma isso diretamente.', es: 'El texto lo dice directamente.' } },
  { id: nid(), cefr: 'A2', cat: 'reading', translated: true,
    q: { en: 'Read: "El tren sale a las 8 pero yo llego a la estación a las 7:30 para no perderlo." — Why does the person arrive early?', pt: 'Leia: "El tren sale a las 8 pero yo llego a la estación a las 7:30 para no perderlo." — Por que a pessoa chega cedo?', es: 'Lee: "El tren sale a las 8 pero yo llego a la estación a las 7:30 para no perderlo." — ¿Por qué llega temprano la persona?' },
    opts: { en: ['To have breakfast', 'To not miss the train', 'To buy a ticket', 'To meet a friend'], pt: ['Para tomar café da manhã', 'Para não perder o trem', 'Para comprar uma passagem', 'Para encontrar um amigo'], es: ['Para desayunar', 'Para no perder el tren', 'Para comprar un billete', 'Para ver a un amigo'] },
    answer: 1, expl: { en: 'The text explains the reason.', pt: 'O texto explica a razão.', es: 'Explica la razón de llegar temprano.' } },
  { id: nid(), cefr: 'A2', cat: 'reading', translated: true,
    q: { en: 'Read: "Carlos trabaja de lunes a viernes. Los fines de semana descansa y visita a sus abuelos." — What does Carlos do on weekends?', pt: 'Leia: "Carlos trabaja de lunes a viernes. Los fines de semana descansa y visita a sus abuelos." — O que Carlos faz nos fins de semana?', es: 'Lee: "Carlos trabaja de lunes a viernes. Los fines de semana descansa y visita a sus abuelos." — ¿Qué hace Carlos los fines de semana?' },
    opts: { en: ['He works', 'He rests and visits his grandparents', 'He studies', 'He travels'], pt: ['Ele trabalha', 'Ele descansa e visita os avós', 'Ele estuda', 'Ele viaja'], es: ['Trabaja', 'Descansa y visita a sus abuelos', 'Estudia', 'Viaja'] },
    answer: 1, expl: { en: 'The text says this directly.', pt: 'O texto diz isso diretamente.', es: 'El texto lo dice directamente.' } },
  { id: nid(), cefr: 'A2', cat: 'reading', translated: true,
    q: { en: 'Read: "La tienda abre a las 9 y cierra a las 21, excepto los domingos que cierra a las 14." — At what time does the store close on Sundays?', pt: 'Leia: "La tienda abre a las 9 y cierra a las 21, excepto los domingos que cierra a las 14." — A que horas a loja fecha aos domingos?', es: 'Lee: "La tienda abre a las 9 y cierra a las 21, excepto los domingos que cierra a las 14." — ¿A qué hora cierra la tienda los domingos?' },
    opts: { en: ['9', '21', '14', '12'], pt: ['9', '21', '14', '12'], es: ['9', '21', '14', '12'] },
    answer: 2, expl: { en: 'The text gives the Sunday exception.', pt: 'O texto dá a exceção do domingo.', es: 'El texto especifica la excepción del domingo.' } },

  { id: nid(), cefr: 'A2', cat: 'expressions', translated: true,
    q: { en: 'What does "tener hambre" mean?', pt: 'O que significa "tener hambre"?', es: '¿Qué significa "tener hambre"?' },
    opts: { en: ['To be hungry', 'To be tired', 'To be in a hurry', 'To be afraid'], pt: ['Estar com fome', 'Estar cansado', 'Estar com pressa', 'Estar com medo'], es: ['Tener ganas de comer', 'Estar cansado', 'Tener prisa', 'Tener miedo'] },
    answer: 0, expl: { en: 'An expression with "tener" + noun.', pt: 'Expressão com "tener" + substantivo.', es: 'Expresión con "tener" + sustantivo.' } },
  { id: nid(), cefr: 'A2', cat: 'expressions', translated: true,
    q: { en: 'What does "hace buen tiempo" mean?', pt: 'O que significa "hace buen tiempo"?', es: '¿Qué significa "hace buen tiempo"?' },
    opts: { en: ["It's good weather", "It's late", "It's a good time to leave", "It's been a while"], pt: ['O tempo está bom (clima)', 'Está tarde', 'É um bom momento para sair', 'Já faz um tempo'], es: ['El clima está agradable', 'Es tarde', 'Es buen momento para irse', 'Ha pasado tiempo'] },
    answer: 0, expl: { en: 'Refers to the weather.', pt: 'Refere-se ao clima.', es: 'Se refiere al clima.' } },
  { id: nid(), cefr: 'A2', cat: 'expressions', translated: true,
    q: { en: 'What does "estar de buen humor" mean?', pt: 'O que significa "estar de buen humor"?', es: '¿Qué significa "estar de buen humor"?' },
    opts: { en: ['To be in a good mood', 'To be sick', 'To be busy', 'To be late'], pt: ['Estar de bom humor', 'Estar doente', 'Estar ocupado', 'Estar atrasado'], es: ['Estar de buen ánimo', 'Estar enfermo', 'Estar ocupado', 'Llegar tarde'] },
    answer: 0, expl: { en: '"Humor" here means mood.', pt: '"Humor" aqui significa estado de ânimo.', es: '"Humor" aquí significa estado de ánimo.' } },
  { id: nid(), cefr: 'A2', cat: 'expressions', translated: true,
    q: { en: 'What does "tener prisa" mean?', pt: 'O que significa "tener prisa"?', es: '¿Qué significa "tener prisa"?' },
    opts: { en: ['To be in a hurry', 'To be hungry', 'To be sleepy', 'To be bored'], pt: ['Estar com pressa', 'Estar com fome', 'Estar com sono', 'Estar entediado'], es: ['Tener urgencia por hacer algo', 'Tener hambre', 'Tener sueño', 'Estar aburrido'] },
    answer: 0, expl: { en: '"Prisa" means urgency to do something.', pt: '"Prisa" significa urgência para fazer algo.', es: '"Prisa" = urgencia por hacer algo rápido.' } },

  // ══════════════════════════ B1 (Spanish only) ══════════════════════════
  { id: nid(), cefr: 'B1', cat: 'vocab', translated: false,
    q: '¿Qué significa "darse cuenta de algo"?',
    opts: ['Sorprenderse mucho', 'Percatarse o notar algo', 'Olvidar algo por completo', 'Estar de acuerdo con algo'],
    answer: 1, expl: 'Sinónimo de notar o percatarse.' },
  { id: nid(), cefr: 'B1', cat: 'vocab', translated: false,
    q: '¿Qué significa "echar de menos a alguien"?',
    opts: ['Extrañar a alguien', 'Enojarse con alguien', 'Despedirse de alguien', 'Visitar a alguien'],
    answer: 0, expl: 'Sinónimo de extrañar.' },
  { id: nid(), cefr: 'B1', cat: 'vocab', translated: false,
    q: '¿Qué significa "cumplir con una tarea"?',
    opts: ['Olvidar una tarea', 'Completar o realizar una tarea', 'Rechazar una tarea', 'Posponer una tarea'],
    answer: 1, expl: '"Cumplir" implica llevar a cabo algo.' },
  { id: nid(), cefr: 'B1', cat: 'vocab', translated: false,
    q: '¿Qué significa "estar al tanto de algo"?',
    opts: ['Estar informado sobre algo', 'Estar cansado de algo', 'Estar en desacuerdo', 'Estar distraído'],
    answer: 0, expl: '"Al tanto" = con información actualizada.' },

  { id: nid(), cefr: 'B1', cat: 'grammar', translated: false,
    q: 'Completa: "Cuando era niño, ___ (jugar) en el parque todos los días."',
    opts: ['jugué', 'jugaba', 'he jugado', 'jugaré'],
    answer: 1, expl: 'Imperfecto para hábitos pasados.' },
  { id: nid(), cefr: 'B1', cat: 'grammar', translated: false,
    q: 'Completa: "No creo que ella ___ (tener) razón."',
    opts: ['tiene', 'tenga', 'tendrá', 'tuvo'],
    answer: 1, expl: '"No creer que" pide subjuntivo.' },
  { id: nid(), cefr: 'B1', cat: 'grammar', translated: false,
    q: 'Completa: "Es importante que ___ (llegar) a tiempo."',
    opts: ['llegas', 'llegues', 'llegarás', 'llegaste'],
    answer: 1, expl: 'Expresiones de importancia piden subjuntivo.' },
  { id: nid(), cefr: 'B1', cat: 'grammar', translated: false,
    q: 'Completa: "Mientras ___ (cocinar) la cena, escuchaba música."',
    opts: ['cocino', 'cocinaba', 'cocine', 'cocinaré'],
    answer: 1, expl: 'Acción simultánea en pasado = imperfecto.' },

  { id: nid(), cefr: 'B1', cat: 'reading', translated: false,
    q: 'Lee: "Aunque llovía mucho, decidimos salir a caminar porque llevábamos varios días encerrados en casa." ¿Por qué salieron a caminar?',
    opts: ['Porque hacía sol', 'Porque llevaban días encerrados en casa', 'Porque tenían que ir de compras', 'Porque un amigo los invitó'],
    answer: 1, expl: 'El texto da la razón explícita.' },
  { id: nid(), cefr: 'B1', cat: 'reading', translated: false,
    q: 'Lee: "El nuevo horario entra en vigor a partir del lunes que viene, según anunció la empresa esta mañana." ¿Cuándo empieza el nuevo horario?',
    opts: ['Este mismo lunes', 'El lunes que viene', 'El próximo mes', 'No se sabe todavía'],
    answer: 1, expl: 'El texto anuncia la fecha exacta.' },
  { id: nid(), cefr: 'B1', cat: 'reading', translated: false,
    q: 'Lee: "Muchos estudiantes prefieren estudiar por la mañana porque se concentran mejor." ¿Por qué prefieren estudiar por la mañana?',
    opts: ['Porque tienen más tiempo', 'Porque se concentran mejor', 'Porque hace menos calor', 'Porque no hay clases'],
    answer: 1, expl: 'El texto da la razón directamente.' },
  { id: nid(), cefr: 'B1', cat: 'reading', translated: false,
    q: 'Lee: "A pesar del tráfico, llegamos a la reunión a tiempo gracias a que salimos con antelación." ¿Por qué llegaron a tiempo?',
    opts: ['Porque no había tráfico', 'Porque salieron con antelación', 'Porque tomaron un atajo', 'Porque cancelaron la reunión'],
    answer: 1, expl: '"Con antelación" = con anticipación.' },

  { id: nid(), cefr: 'B1', cat: 'expressions', translated: false,
    q: '¿Qué significa "estar como pez en el agua"?',
    opts: ['Sentirse muy cómodo en una situación', 'Estar muy nervioso', 'Nadar muy bien', 'Tener mucha sed'],
    answer: 0, expl: 'Expresión de comodidad en una situación.' },
  { id: nid(), cefr: 'B1', cat: 'expressions', translated: false,
    q: '¿Qué significa "ponerse las pilas"?',
    opts: ['Descansar un poco', 'Esforzarse y ponerse a trabajar en serio', 'Comprar baterías', 'Enfadarse con alguien'],
    answer: 1, expl: 'Expresión de motivarse a trabajar.' },
  { id: nid(), cefr: 'B1', cat: 'expressions', translated: false,
    q: '¿Qué significa "costar un ojo de la cara"?',
    opts: ['Ser muy barato', 'Ser muy caro', 'Ser gratis', 'Ser de mala calidad'],
    answer: 1, expl: 'Expresión que indica un precio elevado.' },
  { id: nid(), cefr: 'B1', cat: 'expressions', translated: false,
    q: '¿Qué significa "no pegar ojo"?',
    opts: ['Dormir profundamente', 'No poder dormir', 'Cerrar los ojos', 'Tener sueño ligero'],
    answer: 1, expl: '"Pegar ojo" = conciliar el sueño; con "no", lo contrario.' },

  // ══════════════════════════ B2 (Spanish only) ══════════════════════════
  { id: nid(), cefr: 'B2', cat: 'vocab', translated: false,
    q: '¿Qué significa "llevar a cabo un proyecto"?',
    opts: ['Cancelar un proyecto', 'Ejecutar o realizar un proyecto', 'Empezar a pensar en un proyecto', 'Rechazar un proyecto'],
    answer: 1, expl: 'Sinónimo de ejecutar o realizar.' },
  { id: nid(), cefr: 'B2', cat: 'vocab', translated: false,
    q: '¿Qué significa "sopesar las opciones"?',
    opts: ['Elegir al azar', 'Evaluar cuidadosamente las alternativas', 'Ignorar las alternativas', 'Rechazar todas las opciones'],
    answer: 1, expl: 'Sinónimo de evaluar cuidadosamente.' },
  { id: nid(), cefr: 'B2', cat: 'vocab', translated: false,
    q: '¿Qué significa "plantear una propuesta"?',
    opts: ['Rechazar una propuesta', 'Presentar o proponer una idea', 'Aceptar sin condiciones', 'Cancelar una reunión'],
    answer: 1, expl: '"Plantear" = exponer o proponer algo.' },
  { id: nid(), cefr: 'B2', cat: 'vocab', translated: false,
    q: '¿Qué significa "repercutir en algo"?',
    opts: ['No tener ningún efecto', 'Tener consecuencias sobre algo', 'Ser indiferente a algo', 'Detener un proceso'],
    answer: 1, expl: '"Repercutir" implica un efecto o consecuencia.' },

  { id: nid(), cefr: 'B2', cat: 'grammar', translated: false,
    q: 'Completa: "Si hubiera sabido la verdad, ___ (actuar) de otra manera."',
    opts: ['actuaría', 'habría actuado', 'actué', 'actuara'],
    answer: 1, expl: 'Condicional compuesto para hipótesis pasadas.' },
  { id: nid(), cefr: 'B2', cat: 'grammar', translated: false,
    q: 'Completa: "Por muy difícil que ___ (ser), lo lograremos."',
    opts: ['es', 'sea', 'será', 'fue'],
    answer: 1, expl: '"Por muy... que" exige subjuntivo.' },
  { id: nid(), cefr: 'B2', cat: 'grammar', translated: false,
    q: 'Completa: "Ojalá ___ (poder) asistir a la boda, pero tengo un viaje."',
    opts: ['puedo', 'pudiera', 'podré', 'pude'],
    answer: 1, expl: 'Deseo poco probable = imperfecto de subjuntivo.' },
  { id: nid(), cefr: 'B2', cat: 'grammar', translated: false,
    q: 'Completa: "Te llamaré en cuanto ___ (terminar) la reunión."',
    opts: ['termino', 'termine', 'terminaré', 'terminé'],
    answer: 1, expl: '"En cuanto" + futuro = subjuntivo presente.' },

  { id: nid(), cefr: 'B2', cat: 'reading', translated: false,
    q: 'Lee: "A pesar de las críticas iniciales, la medida terminó siendo bien recibida por la mayoría de los ciudadanos." ¿Cómo fue recibida la medida al final?',
    opts: ['Con rechazo total', 'Bien, por la mayoría de los ciudadanos', 'Con indiferencia', 'No se menciona'],
    answer: 1, expl: 'El texto lo confirma explícitamente.' },
  { id: nid(), cefr: 'B2', cat: 'reading', translated: false,
    q: 'Lee: "Cabe señalar que el informe, si bien es riguroso, no contempla ciertas variables económicas recientes." ¿Qué le falta al informe?',
    opts: ['Rigor', 'Ciertas variables económicas recientes', 'Datos históricos', 'Conclusiones claras'],
    answer: 1, expl: 'El texto señala la carencia.' },
  { id: nid(), cefr: 'B2', cat: 'reading', translated: false,
    q: 'Lee: "Si bien las ventas aumentaron, los costos de producción también se dispararon considerablemente." ¿Qué pasó con los costos?',
    opts: ['Bajaron', 'Se mantuvieron iguales', 'Aumentaron considerablemente', 'No se mencionan'],
    answer: 2, expl: '"Se dispararon" significa que subieron mucho.' },
  { id: nid(), cefr: 'B2', cat: 'reading', translated: false,
    q: 'Lee: "La empresa, consciente del riesgo, decidió posponer el lanzamiento hasta nuevo aviso." ¿Qué decidió la empresa?',
    opts: ['Cancelar el lanzamiento', 'Posponer el lanzamiento', 'Adelantar el lanzamiento', 'Ignorar el riesgo'],
    answer: 1, expl: '"Posponer" = aplazar para más adelante.' },

  { id: nid(), cefr: 'B2', cat: 'expressions', translated: false,
    q: '¿Qué significa "irse por las ramas"?',
    opts: ['Ser muy directo', 'Desviarse del tema principal', 'Subir a un árbol', 'Tomar una decisión rápida'],
    answer: 1, expl: 'Expresión de desviarse del tema.' },
  { id: nid(), cefr: 'B2', cat: 'expressions', translated: false,
    q: '¿Qué significa "no tener pelos en la lengua"?',
    opts: ['Ser muy tímido', 'Hablar con total franqueza', 'Hablar muy poco', 'Tener problemas de salud'],
    answer: 1, expl: 'Expresión de hablar con franqueza.' },
  { id: nid(), cefr: 'B2', cat: 'expressions', translated: false,
    q: '¿Qué significa "estar entre la espada y la pared"?',
    opts: ['Tener muchas opciones', 'Estar en una situación difícil sin escapatoria', 'Sentirse muy seguro', 'Tomar una decisión fácil'],
    answer: 1, expl: 'Expresión de dilema sin salida fácil.' },
  { id: nid(), cefr: 'B2', cat: 'expressions', translated: false,
    q: '¿Qué significa "poner los puntos sobre las íes"?',
    opts: ['Dejar algo claro y sin ambigüedad', 'Escribir con mala letra', 'Empezar un proyecto', 'Cambiar de opinión'],
    answer: 0, expl: 'Se usa para aclarar algo con precisión.' },

  // ══════════════════════════ C1 (Spanish only) ══════════════════════════
  { id: nid(), cefr: 'C1', cat: 'vocab', translated: false,
    q: '¿Qué significa "paliar los efectos de algo"?',
    opts: ['Empeorar una situación', 'Mitigar o suavizar las consecuencias', 'Ignorar un problema', 'Provocar un efecto'],
    answer: 1, expl: 'Sinónimo de mitigar o aliviar.' },
  { id: nid(), cefr: 'C1', cat: 'vocab', translated: false,
    q: '¿Qué significa "un hito" dentro de un proyecto?',
    opts: ['Un error grave', 'Un logro o punto importante alcanzado', 'Un retraso', 'Un gasto imprevisto'],
    answer: 1, expl: 'Se refiere a un logro importante.' },
  { id: nid(), cefr: 'C1', cat: 'vocab', translated: false,
    q: '¿Qué significa "suscitar polémica"?',
    opts: ['Evitar un conflicto', 'Provocar controversia o debate', 'Resolver un problema', 'Ignorar una crítica'],
    answer: 1, expl: '"Suscitar" = generar o provocar algo.' },
  { id: nid(), cefr: 'C1', cat: 'vocab', translated: false,
    q: '¿Qué significa "un desenlace inesperado"?',
    opts: ['Un final que se veía venir', 'Un final sorprendente', 'Un inicio confuso', 'Una pausa en la trama'],
    answer: 1, expl: '"Desenlace" es el final; "inesperado" = sorpresivo.' },

  { id: nid(), cefr: 'C1', cat: 'grammar', translated: false,
    q: 'Completa: "De haberlo sabido antes, no ___ (cometer) ese error."',
    opts: ['cometería', 'habría cometido', 'cometo', 'cometí'],
    answer: 1, expl: 'Condicional compuesto para hipótesis pasadas.' },
  { id: nid(), cefr: 'C1', cat: 'grammar', translated: false,
    q: 'Completa: "Quienquiera que ___ (ser) el responsable, deberá dar explicaciones."',
    opts: ['es', 'sea', 'fuera', 'será'],
    answer: 1, expl: '"Quienquiera que" exige subjuntivo.' },
  { id: nid(), cefr: 'C1', cat: 'grammar', translated: false,
    q: 'Completa: "Por muchos esfuerzos que ___ (hacer), no logró convencerlos."',
    opts: ['hace', 'hiciera', 'hizo', 'hará'],
    answer: 1, expl: 'Concesión en pasado = imperfecto de subjuntivo.' },
  { id: nid(), cefr: 'C1', cat: 'grammar', translated: false,
    q: 'Completa: "Habría preferido que me lo ___ (decir) antes."',
    opts: ['dices', 'dijeras', 'dirás', 'dijiste'],
    answer: 1, expl: 'Deseo sobre el pasado = imperfecto de subjuntivo.' },

  { id: nid(), cefr: 'C1', cat: 'reading', translated: false,
    q: 'Lee: "No deja de sorprender que, pese a la abundancia de recursos, la región siga enfrentando carencias tan básicas." ¿Qué resulta sorprendente?',
    opts: ['La falta de recursos', 'Que haya carencias pese a los recursos disponibles', 'El crecimiento económico', 'La ausencia de problemas'],
    answer: 1, expl: 'El texto expresa sorpresa por la contradicción.' },
  { id: nid(), cefr: 'C1', cat: 'reading', translated: false,
    q: 'Lee: "El estudio, lejos de zanjar el debate, ha reavivado antiguas discrepancias entre los expertos." ¿Qué provocó el estudio?',
    opts: ['Cerró el debate por completo', 'Reavivó discrepancias entre expertos', 'No tuvo ningún impacto', 'Confirmó el consenso previo'],
    answer: 1, expl: '"Lejos de zanjar" indica lo contrario a resolver.' },
  { id: nid(), cefr: 'C1', cat: 'reading', translated: false,
    q: 'Lee: "El ponente, lejos de eludir las preguntas incómodas, las abordó con notable franqueza." ¿Cómo respondió el ponente?',
    opts: ['Evitó las preguntas', 'Las respondió con franqueza', 'No permitió preguntas', 'Se mostró evasivo'],
    answer: 1, expl: '"Lejos de eludir" indica lo contrario de evitar.' },
  { id: nid(), cefr: 'C1', cat: 'reading', translated: false,
    q: 'Lee: "Convendría matizar que el crecimiento, aunque real, dista mucho de ser uniforme entre regiones." ¿Qué se dice del crecimiento?',
    opts: ['Es uniforme en todas las regiones', 'No es uniforme entre regiones', 'No existe', 'Es exclusivo de una región'],
    answer: 1, expl: '"Dista mucho de ser uniforme" = no es parejo.' },

  { id: nid(), cefr: 'C1', cat: 'expressions', translated: false,
    q: '¿Qué significa "tirar la toalla"?',
    opts: ['Esforzarse al máximo', 'Rendirse o abandonar algo', 'Limpiar algo con esmero', 'Celebrar una victoria'],
    answer: 1, expl: 'Expresión de rendirse.' },
  { id: nid(), cefr: 'C1', cat: 'expressions', translated: false,
    q: '¿Qué significa "estar en tela de juicio"?',
    opts: ['Estar totalmente confirmado', 'Estar bajo duda o cuestionamiento', 'Estar terminado', 'Estar de moda'],
    answer: 1, expl: 'Expresión de estar en duda.' },
  { id: nid(), cefr: 'C1', cat: 'expressions', translated: false,
    q: '¿Qué significa "dorar la píldora"?',
    opts: ['Decir algo de forma más suave de lo que realmente es', 'Ser muy directo y crudo', 'Tomar una medicina', 'Mentir descaradamente'],
    answer: 0, expl: 'Se usa para suavizar una mala noticia.' },
  { id: nid(), cefr: 'C1', cat: 'expressions', translated: false,
    q: '¿Qué significa "meter la pata"?',
    opts: ['Actuar con cuidado', 'Cometer un error o equivocación', 'Tener mucha suerte', 'Ayudar a alguien'],
    answer: 1, expl: 'Expresión coloquial para un error.' },

  // ══════════════════════════ C2 (Spanish only) ══════════════════════════
  { id: nid(), cefr: 'C2', cat: 'vocab', translated: false,
    q: '¿Qué significa "la avenencia entre las partes"?',
    opts: ['El desacuerdo total', 'El acuerdo o entendimiento mutuo', 'La ruptura de relaciones', 'Un conflicto legal'],
    answer: 1, expl: 'Sinónimo de acuerdo mutuo.' },
  { id: nid(), cefr: 'C2', cat: 'vocab', translated: false,
    q: '¿Qué significa "un resquicio de esperanza"?',
    opts: ['Una posibilidad mínima pero real', 'Una certeza absoluta', 'Una decepción total', 'Un obstáculo insalvable'],
    answer: 0, expl: 'Se refiere a una posibilidad mínima.' },
  { id: nid(), cefr: 'C2', cat: 'vocab', translated: false,
    q: '¿Qué significa "la inflexión de un discurso"?',
    opts: ['El cambio de tono o dirección de un discurso', 'El final abrupto de un discurso', 'La repetición de ideas', 'La duración total del discurso'],
    answer: 0, expl: '"Inflexión" se refiere a un giro o cambio.' },
  { id: nid(), cefr: 'C2', cat: 'vocab', translated: false,
    q: '¿Qué significa "un ápice de duda"?',
    opts: ['Una duda enorme', 'Una mínima cantidad de duda', 'Ninguna duda', 'Una certeza absoluta'],
    answer: 1, expl: '"Ápice" significa una cantidad mínima.' },

  { id: nid(), cefr: 'C2', cat: 'grammar', translated: false,
    q: 'Completa: "De no haber mediado su intervención, el acuerdo ___ (fracasar) irremediablemente."',
    opts: ['fracasaría', 'habría fracasado', 'fracasa', 'fracasó'],
    answer: 1, expl: 'Condicional compuesto para hipótesis pasadas irreales.' },
  { id: nid(), cefr: 'C2', cat: 'grammar', translated: false,
    q: 'Completa: "Por más que se ___ (empeñar) en negarlo, los hechos hablan por sí solos."',
    opts: ['empeña', 'empeñe', 'empeñará', 'empeñó'],
    answer: 1, expl: '"Por más que" exige subjuntivo.' },
  { id: nid(), cefr: 'C2', cat: 'grammar', translated: false,
    q: 'Completa: "Cualesquiera que ___ (ser) las consecuencias, asumiremos la responsabilidad."',
    opts: ['son', 'sean', 'serán', 'fueron'],
    answer: 1, expl: '"Cualesquiera que" exige subjuntivo.' },
  { id: nid(), cefr: 'C2', cat: 'grammar', translated: false,
    q: 'Completa: "Para cuando se publiquen los resultados, ya ___ (cambiar) la situación."',
    opts: ['cambia', 'habrá cambiado', 'cambió', 'cambiará'],
    answer: 1, expl: 'Futuro compuesto para una acción anterior a otra futura.' },

  { id: nid(), cefr: 'C2', cat: 'reading', translated: false,
    q: 'Lee: "No es baladí el hecho de que, tras años de estancamiento, la cifra haya repuntado de forma tan notoria." ¿Qué se destaca del hecho mencionado?',
    opts: ['Que carece de importancia', 'Que tiene relevancia pese a lo inesperado', 'Que es un error de cálculo', 'Que ocurre todos los años'],
    answer: 1, expl: '"No es baladí" significa que sí es importante.' },
  { id: nid(), cefr: 'C2', cat: 'reading', translated: false,
    q: 'Lee: "Al margen de las discrepancias metodológicas, ambos estudios convergen en sus conclusiones principales." ¿En qué coinciden los estudios?',
    opts: ['En la metodología', 'En las conclusiones principales', 'En ningún aspecto', 'En el tamaño de la muestra'],
    answer: 1, expl: '"Convergen" significa que coinciden.' },
  { id: nid(), cefr: 'C2', cat: 'reading', translated: false,
    q: 'Lee: "Lejos de zanjarse, la controversia en torno a dicha reforma no ha hecho sino agravarse con el correr de los meses." ¿Qué ha pasado con la controversia?',
    opts: ['Se resolvió rápidamente', 'Se ha agravado con el tiempo', 'Nunca existió', 'Fue ignorada por los medios'],
    answer: 1, expl: 'El texto indica que ha empeorado con el tiempo.' },
  { id: nid(), cefr: 'C2', cat: 'reading', translated: false,
    q: 'Lee: "Súmese a ello la circunstancia, nada desdeñable, de que los plazos originales resultaron a todas luces insuficientes." ¿Qué se dice de los plazos originales?',
    opts: ['Fueron generosos', 'Resultaron insuficientes', 'No se establecieron nunca', 'Fueron ideales'],
    answer: 1, expl: '"Insuficientes" es sinónimo directo.' },

  { id: nid(), cefr: 'C2', cat: 'expressions', translated: false,
    q: '¿Qué significa "llover sobre mojado"?',
    opts: ['Que algo bueno se repite', 'Que un problema se suma a otro ya existente', 'Que hace muy buen clima', 'Que todo está en calma'],
    answer: 1, expl: 'Expresión de un problema que se suma a otro.' },
  { id: nid(), cefr: 'C2', cat: 'expressions', translated: false,
    q: '¿Qué significa "dar en el clavo"?',
    opts: ['Equivocarse por completo', 'Acertar exactamente en lo que se buscaba', 'Actuar con violencia', 'Tardar mucho en decidir'],
    answer: 1, expl: 'Expresión de acertar exactamente.' },
  { id: nid(), cefr: 'C2', cat: 'expressions', translated: false,
    q: '¿Qué significa "estar curado de espanto"?',
    opts: ['Sorprenderse fácilmente', 'Ya no sorprenderse por nada, tras haberlo visto todo', 'Tener mucho miedo', 'Estar enfermo'],
    answer: 1, expl: 'Se usa cuando alguien ya no se asombra por nada.' },
  { id: nid(), cefr: 'C2', cat: 'expressions', translated: false,
    q: '¿Qué significa "a río revuelto, ganancia de pescadores"?',
    opts: ['En momentos de confusión, algunos se aprovechan', 'Es mejor pescar en aguas tranquilas', 'Nunca hay que aprovecharse de nadie', 'Los pescadores no ganan dinero'],
    answer: 0, expl: 'Refrán sobre aprovecharse del caos o la confusión.' },
];

// ── SCORING LOGIC ─────────────────────────────────────────────────────────────

function getLevelScores(answers) {
  const scores = {};
  CEFR_ORDER.forEach(lvl => {
    const qs = QUESTIONS.filter(q => q.cefr === lvl);
    const correct = qs.filter(q => answers[q.id] === q.answer).length;
    scores[lvl] = { correct, total: qs.length };
  });
  return scores;
}

// CEFR placement: your level is the highest tier you clear with >=60%,
// stopping at the first tier you don't clear (sequential, like a real
// placement exam — mirrors how CEFR levels build on each other).
function getLevel(levelScores) {
  let result = 'A1';
  for (const lvl of CEFR_ORDER) {
    const { correct, total } = levelScores[lvl];
    if (correct / total >= 0.6) result = lvl;
    else break;
  }
  return result;
}

function getBreakdown(answers) {
  const breakdown = {};
  CATS.forEach(cat => {
    const qs = QUESTIONS.filter(q => q.cat === cat);
    const correct = qs.filter(q => answers[q.id] === q.answer).length;
    breakdown[cat] = { correct, total: qs.length };
  });
  return breakdown;
}

function getCatStatus(pct) {
  if (pct >= 85) return 'excellent';
  if (pct >= 65) return 'good';
  return 'weak';
}

const STATUS_COLOR = { excellent: '#16a34a', good: '#0284c7', weak: '#dc2626' };
const STATUS_BG = { excellent: '#dcfce7', good: '#dbeafe', weak: '#fee2e2' };

function getQuarter(date) {
  return Math.ceil((date.getMonth() + 1) / 3);
}

function getQuarterStartMonthName(quarter, year, lang) {
  const startMonth = (quarter - 1) * 3; // 0-indexed month of quarter start
  const d = new Date(year, startMonth, 1);
  return d.toLocaleString(lang === 'es' ? 'es-ES' : lang === 'en' ? 'en-GB' : 'pt-BR', { month: 'long' });
}

const testStyles = `
  .test-opt { width: 100%; text-align: left; padding: 14px 18px; border-radius: 14px; border: 2px solid #e2e8f0; background: white; font-weight: 600; font-size: 0.9rem; color: #0f172a; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.15s; margin-bottom: 10px; display: block; }
  .test-opt:hover { border-color: #dc2626; background: #fff1f2; color: #dc2626; }
  .test-opt.correct { border-color: #86efac; background: #f0fdf4; color: #15803d; }
  .test-opt.wrong { border-color: #fecdd3; background: #fff1f2; color: #be123c; }
  .bar-wrap { background: #f1f5f9; border-radius: 99px; height: 10px; overflow: hidden; flex: 1; }
  .bar-fill { height: 100%; border-radius: 99px; transition: width 0.6s cubic-bezier(0.16,1,0.3,1); }
  .review-toggle { padding: 8px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: white; font-weight: 700; font-size: 0.78rem; color: #475569; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; }
  .review-toggle.active { background: #0f172a; color: white; border-color: #0f172a; }
`;

export default function LevelTestES({ session, lang = 'pt', onBack }) {
  const T = S[lang] || S['pt'];
  const [screen, setScreen] = useState('loading'); // loading | locked | start | test | result
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [breakdown, setBreakdown] = useState(null);
  const [levelScores, setLevelScores] = useState(null);
  const [levelResult, setLevelResult] = useState(null);
  const [finalAnswersState, setFinalAnswersState] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [history, setHistory] = useState([]);
  const [lastTest, setLastTest] = useState(null);
  const [reviewCat, setReviewCat] = useState('vocab');
  const [reviewMode, setReviewMode] = useState('wrong'); // wrong | all

  const loadHistory = useCallback(async () => {
    const { data } = await supabase
      .from('level_tests')
      .select('*')
      .eq('student_id', session.id)
      .order('created_at', { ascending: false });
    const tests = data || [];
    setHistory(tests);
    const now = new Date();
    const thisQuarter = getQuarter(now);
    const thisYear = now.getFullYear();
    const alreadyTook = tests.find(t => t.quarter === thisQuarter && t.year === thisYear);
    if (alreadyTook) {
      setLastTest(alreadyTook);
      setScreen('locked');
    } else {
      setScreen('start');
    }
  }, [session.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const startTest = () => {
    setAnswers({});
    setSelected(null);
    setRevealed(false);
    setScore(0);
    setCurrent(0);
    setScreen('test');
  };

  const pick = (optIdx) => {
    if (revealed) return;
    setSelected(optIdx);
    setRevealed(true);
    const q = QUESTIONS[current];
    const correct = optIdx === q.answer;
    if (correct) setScore(s => s + 1);
    setAnswers(prev => ({ ...prev, [q.id]: optIdx }));
    setTimeout(() => {
      if (current + 1 >= QUESTIONS.length) {
        const finalAnswers = { ...answers, [q.id]: optIdx };
        const bd = getBreakdown(finalAnswers);
        const ls = getLevelScores(finalAnswers);
        const finalScore = Object.values(bd).reduce((a, c) => a + c.correct, 0);
        const lvl = getLevel(ls);
        setBreakdown(bd);
        setLevelScores(ls);
        setLevelResult(lvl);
        setScore(finalScore);
        setFinalAnswersState(finalAnswers);
        setScreen('result');
      } else {
        setCurrent(c => c + 1);
        setSelected(null);
        setRevealed(false);
      }
    }, 1800);
  };

  const saveResult = async () => {
    setSaving(true);
    const now = new Date();
    await supabase.from('level_tests').insert({
      student_id: session.id,
      score,
      total: QUESTIONS.length,
      level_result: levelResult,
      breakdown,
      level_scores: levelScores,
      answers: finalAnswersState,
      quarter: getQuarter(now),
      year: now.getFullYear(),
    });
    setSaving(false);
    setSaved(true);
    loadHistory();
  };

  const showSavedResult = () => {
    if (!lastTest) return;
    setScore(lastTest.score);
    setBreakdown(lastTest.breakdown);
    setLevelScores(lastTest.level_scores || null);
    setLevelResult(lastTest.level_result);
    setFinalAnswersState(lastTest.answers || {});
    setSaved(true);
    setScreen('result');
  };

  // ── LOADING ────────────────────────────────────────────────────────────────
  if (screen === 'loading') return (
    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#94a3b8', fontWeight: '600' }}>
      {lang === 'es' ? 'Cargando...' : lang === 'en' ? 'Loading...' : 'Carregando...'}
    </div>
  );

  // ── LOCKED ─────────────────────────────────────────────────────────────────
  if (screen === 'locked') {
    const now = new Date();
    const thisQuarter = getQuarter(now);
    const nextQuarter = thisQuarter === 4 ? 1 : thisQuarter + 1;
    const nextYear = thisQuarter === 4 ? now.getFullYear() + 1 : now.getFullYear();
    const nextMonthName = getQuarterStartMonthName(nextQuarter, nextYear, lang);
    return (
      <>
        <style>{testStyles}</style>
        <div style={{ padding: '24px 20px 80px', maxWidth: '600px', margin: '0 auto' }}>
          <button onClick={onBack} style={{ background: '#f1f5f9', border: 'none', borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', marginBottom: '28px' }}>{T.back}</button>
          <div style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)', borderRadius: '24px', padding: '32px 28px', textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔒</div>
            <div style={{ fontWeight: '900', color: 'white', fontSize: '1.2rem', marginBottom: '8px' }}>{T.alreadyTook}</div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', fontWeight: '600', marginBottom: '24px' }}>{T.nextAvailable} {nextMonthName}</div>
            <button onClick={showSavedResult} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '12px', padding: '13px 24px', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{T.viewResult}</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: 'linear-gradient(135deg,#fff7ed,#fef3c7)', border: '1.5px solid #fde68a', borderRadius: '16px', padding: '14px 16px', boxShadow: '0 2px 10px rgba(217,119,6,0.08)' }}>
            <span style={{ fontSize: '1.15rem', flexShrink: 0 }}>ℹ️</span>
            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#92400e', lineHeight: 1.4 }}>{T.retakeInfo}</span>
          </div>
        </div>
      </>
    );
  }

  // ── START SCREEN ────────────────────────────────────────────────────────────
  if (screen === 'start') return (
    <>
      <style>{testStyles}</style>
      <div style={{ padding: '24px 20px 80px', maxWidth: '600px', margin: '0 auto' }}>
        <button onClick={onBack} style={{ background: '#f1f5f9', border: 'none', borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', marginBottom: '20px' }}>{T.back}</button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: 'linear-gradient(135deg,#fff7ed,#fef3c7)', border: '1.5px solid #fde68a', borderRadius: '16px', padding: '14px 16px', marginBottom: '20px', boxShadow: '0 2px 10px rgba(217,119,6,0.08)' }}>
          <span style={{ fontSize: '1.15rem', flexShrink: 0 }}>ℹ️</span>
          <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#92400e', lineHeight: 1.4 }}>{T.retakeInfo}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: '16px', padding: '14px 16px', marginBottom: '20px' }}>
          <span style={{ fontSize: '1.15rem', flexShrink: 0 }}>🇪🇸</span>
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1e40af', lineHeight: 1.4 }}>{T.contentNote}</span>
        </div>

        <div style={{ background: 'linear-gradient(135deg,#dc2626 0%,#b91c1c 60%,#7f1d1d 100%)', borderRadius: '24px', padding: '36px 28px', textAlign: 'center', marginBottom: '24px', boxShadow: '0 12px 40px rgba(220,38,38,0.3)' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🎯</div>
          <div style={{ fontWeight: '900', color: 'white', fontSize: '1.5rem', marginBottom: '8px', lineHeight: 1.2 }}>{T.title}</div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.88rem', fontWeight: '600', marginBottom: '28px' }}>{T.subtitle}</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '18px', marginBottom: '28px', flexWrap: 'wrap' }}>
            {[['📝', String(TOTAL_QUESTIONS), lang === 'es' ? 'preguntas' : lang === 'en' ? 'questions' : 'perguntas'],
              ['⏱️', '~45', lang === 'es' ? 'minutos' : lang === 'en' ? 'minutes' : 'minutos'],
              ['📶', '6', lang === 'es' ? 'niveles MCER' : lang === 'en' ? 'CEFR levels' : 'níveis QECR'],
              ['📊', '4', lang === 'es' ? 'categorías' : lang === 'en' ? 'categories' : 'categorias']
            ].map(([icon, val, label]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.4rem' }}>{icon}</div>
                <div style={{ fontWeight: '900', color: 'white', fontSize: '1.3rem', lineHeight: 1 }}>{val}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', fontWeight: '700' }}>{label}</div>
              </div>
            ))}
          </div>
          <button onClick={startTest} style={{ background: 'white', color: '#dc2626', border: 'none', borderRadius: '14px', padding: '16px 36px', fontWeight: '900', fontSize: '1.05rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>{T.start} →</button>
        </div>

        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px', border: '1.5px solid #f1f5f9' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>
            {lang === 'es' ? '📶 Niveles MCER evaluados' : lang === 'en' ? '📶 CEFR levels assessed' : '📶 Níveis do QECR avaliados'}
          </div>
          {CEFR_ORDER.map(lvl => {
            const lv = T.levels[lvl];
            return (
              <div key={lvl} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ background: lv.bg, color: lv.color, borderRadius: '8px', padding: '3px 9px', fontSize: '0.72rem', fontWeight: '900', minWidth: '30px', textAlign: 'center' }}>{lv.label}</span>
                <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.88rem' }}>{lv.sublabel}</span>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '600', marginLeft: 'auto' }}>{QUESTIONS_PER_LEVEL} {lang === 'es' ? 'preguntas' : lang === 'en' ? 'questions' : 'perguntas'}</span>
              </div>
            );
          })}
        </div>

        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px', border: '1.5px solid #f1f5f9' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>
            {lang === 'es' ? '📊 Categorías evaluadas' : lang === 'en' ? '📊 Categories assessed' : '📊 Categorias avaliadas'}
          </div>
          {CATS.map(cat => (
            <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <span style={{ fontSize: '1rem', width: '24px' }}>{cat === 'vocab' ? '📚' : cat === 'grammar' ? '✏️' : cat === 'reading' ? '👁️' : '🔗'}</span>
              <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.88rem' }}>{T.categories[cat]}</span>
              <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '600', marginLeft: 'auto' }}>{CEFR_ORDER.length * QUESTIONS_PER_CAT_PER_LEVEL} {lang === 'es' ? 'preguntas' : lang === 'en' ? 'questions' : 'perguntas'}</span>
            </div>
          ))}
        </div>

        {history.length > 0 && (
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1.5px solid #f1f5f9' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>{T.history}</div>
            {history.slice(0, 3).map((t, i) => {
              const lv = T.levels[t.level_result] || T.levels.A1;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: i < Math.min(history.length, 3) - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <span style={{ background: lv.bg, color: lv.color, borderRadius: '8px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '800' }}>{lv.label}</span>
                  <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.88rem' }}>{t.score}/{t.total}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: '600', marginLeft: 'auto' }}>{new Date(t.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-BR')}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  // ── TEST SCREEN ─────────────────────────────────────────────────────────────
  if (screen === 'test') {
    const q = QUESTIONS[current];
    const opts = q.translated ? (q.opts[lang] || q.opts.es) : q.opts;
    const qText = q.translated ? (q.q[lang] || q.q.es) : q.q;
    const pct = Math.round((current / QUESTIONS.length) * 100);
    const lv = T.levels[q.cefr];

    return (
      <>
        <style>{testStyles}</style>
        <div style={{ padding: '24px 20px 80px', maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#64748b' }}>{T.question} {current + 1} {T.of} {QUESTIONS.length}</div>
            <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#dc2626' }}>{score} ✅</div>
          </div>
          <div style={{ height: '6px', background: '#fee2e2', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg,#dc2626,#b91c1c)', borderRadius: '99px', transition: 'width 0.3s' }} />
          </div>
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '20px 22px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ background: lv.bg, color: lv.color, borderRadius: '6px', padding: '2px 8px', fontSize: '0.68rem', fontWeight: '900' }}>{lv.label}</span>
              <span style={{ fontSize: '0.65rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px' }}>{T.categories[q.cat]}</span>
            </div>
            <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '1rem', lineHeight: 1.5 }}>{qText}</div>
          </div>
          <div>
            {opts.map((opt, i) => {
              let cls = 'test-opt';
              if (revealed) {
                if (i === q.answer) cls += ' correct';
                else if (i === selected && i !== q.answer) cls += ' wrong';
              }
              return (
                <button key={i} className={cls} onClick={() => pick(i)} disabled={revealed}>
                  <span style={{ fontWeight: '800', color: '#94a3b8', marginRight: '10px', fontSize: '0.8rem' }}>{String.fromCharCode(65 + i)}.</span>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  // ── RESULT SCREEN ───────────────────────────────────────────────────────────
  if (screen === 'result' && breakdown && levelResult) {
    const lv = T.levels[levelResult];
    const pct = Math.round((score / QUESTIONS.length) * 100);

    // Build review list for the selected category
    const catQuestions = QUESTIONS.filter(q => q.cat === reviewCat);
    const catReviewItems = catQuestions.map(q => {
      const userIdx = finalAnswersState[q.id];
      const isCorrect = userIdx === q.answer;
      const opts = q.translated ? (q.opts[lang] || q.opts.es) : q.opts;
      const qText = q.translated ? (q.q[lang] || q.q.es) : q.q;
      const explText = q.translated ? (q.expl[lang] || q.expl.es) : q.expl;
      return { q, isCorrect, userIdx, opts, qText, explText };
    });
    const visibleReviewItems = reviewMode === 'wrong' ? catReviewItems.filter(r => !r.isCorrect) : catReviewItems;

    return (
      <>
        <style>{testStyles}</style>
        <div style={{ padding: '24px 20px 80px', maxWidth: '600px', margin: '0 auto' }}>
          <button onClick={onBack} style={{ background: '#f1f5f9', border: 'none', borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', marginBottom: '24px' }}>{T.back}</button>

          {/* Level result hero */}
          <div style={{ background: `linear-gradient(135deg,${lv.color},${lv.color}cc)`, borderRadius: '24px', padding: '32px 28px', textAlign: 'center', marginBottom: '20px', boxShadow: `0 12px 40px ${lv.color}40` }}>
            <div style={{ fontSize: '0.75rem', fontWeight: '800', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>{T.result}</div>
            <div style={{ fontWeight: '900', color: 'white', fontSize: '2.4rem', lineHeight: 1, marginBottom: '4px' }}>{lv.label}</div>
            <div style={{ fontWeight: '700', color: 'rgba(255,255,255,0.75)', fontSize: '1rem', marginBottom: '20px' }}>{lv.sublabel}</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', marginBottom: '20px' }}>
              <div>
                <div style={{ fontWeight: '900', color: 'white', fontSize: '2.5rem', lineHeight: 1 }}>{score}/{QUESTIONS.length}</div>
                <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase' }}>{T.score}</div>
              </div>
              <div>
                <div style={{ fontWeight: '900', color: 'white', fontSize: '2.5rem', lineHeight: 1 }}>{pct}%</div>
                <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase' }}>{T.level}</div>
              </div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '12px 16px', fontSize: '0.85rem', fontWeight: '600', color: 'white', lineHeight: 1.5 }}>{lv.desc}</div>
          </div>

          {/* CEFR ladder breakdown */}
          {levelScores && (
            <div style={{ background: 'white', borderRadius: '20px', padding: '24px', marginBottom: '16px', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ fontWeight: '900', color: '#0f172a', fontSize: '0.95rem', marginBottom: '4px' }}>{T.ladder}</div>
              <div style={{ fontWeight: '600', color: '#94a3b8', fontSize: '0.76rem', marginBottom: '16px' }}>{T.ladderSub}</div>
              {CEFR_ORDER.map(cefrLvl => {
                const data = levelScores[cefrLvl];
                const lPct = Math.round((data.correct / data.total) * 100);
                const lColor = lPct >= 60 ? '#16a34a' : lPct >= 40 ? '#d97706' : '#dc2626';
                const lvDef = T.levels[cefrLvl];
                return (
                  <div key={cefrLvl} style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.88rem' }}>
                        <span style={{ background: lvDef.bg, color: lvDef.color, borderRadius: '6px', padding: '1px 7px', fontSize: '0.7rem', fontWeight: '900', marginRight: '8px' }}>{cefrLvl}</span>
                        {lvDef.sublabel}
                      </span>
                      <span style={{ fontWeight: '800', color: lColor, fontSize: '0.85rem' }}>{data.correct}/{data.total} ({lPct}%)</span>
                    </div>
                    <div className="bar-wrap">
                      <div className="bar-fill" style={{ width: `${lPct}%`, background: lColor }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Category accuracy cards + tips */}
          <div style={{ background: 'white', borderRadius: '20px', padding: '24px', marginBottom: '16px', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ fontWeight: '900', color: '#0f172a', fontSize: '0.95rem', marginBottom: '18px' }}>{T.breakdown}</div>
            {CATS.map(cat => {
              const data = breakdown[cat];
              const catPct = Math.round((data.correct / data.total) * 100);
              const stKey = getCatStatus(catPct);
              const stColor = STATUS_COLOR[stKey];
              const stBg = STATUS_BG[stKey];
              return (
                <div key={cat} style={{ marginBottom: '18px', paddingBottom: '18px', borderBottom: cat !== CATS[CATS.length - 1] ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.9rem' }}>
                      {cat === 'vocab' ? '📚' : cat === 'grammar' ? '✏️' : cat === 'reading' ? '👁️' : '🔗'} {T.categories[cat]}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ background: stBg, color: stColor, borderRadius: '8px', padding: '2px 9px', fontSize: '0.68rem', fontWeight: '800' }}>{T.status[stKey]}</span>
                      <span style={{ fontWeight: '800', color: stColor, fontSize: '0.88rem' }}>{data.correct}/{data.total} ({catPct}%)</span>
                    </span>
                  </div>
                  <div className="bar-wrap" style={{ marginBottom: '10px' }}>
                    <div className="bar-fill" style={{ width: `${catPct}%`, background: stColor }} />
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '10px 12px', fontSize: '0.8rem', fontWeight: '600', color: '#475569', lineHeight: 1.5 }}>
                    💡 {T.recs[cat]}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Full answer review */}
          <div style={{ background: 'white', borderRadius: '20px', padding: '24px', marginBottom: '16px', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ fontWeight: '900', color: '#0f172a', fontSize: '0.95rem', marginBottom: '4px' }}>{T.review}</div>
            <div style={{ fontWeight: '600', color: '#94a3b8', fontSize: '0.76rem', marginBottom: '16px' }}>{T.reviewSub}</div>

            {/* Category tabs */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {CATS.map(cat => (
                <button
                  key={cat}
                  onClick={() => setReviewCat(cat)}
                  className={`review-toggle ${reviewCat === cat ? 'active' : ''}`}
                >
                  {cat === 'vocab' ? '📚' : cat === 'grammar' ? '✏️' : cat === 'reading' ? '👁️' : '🔗'} {T.categories[cat]}
                </button>
              ))}
            </div>

            {/* Wrong-only / all toggle */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
              <button onClick={() => setReviewMode('wrong')} className={`review-toggle ${reviewMode === 'wrong' ? 'active' : ''}`}>{T.showWrongOnly}</button>
              <button onClick={() => setReviewMode('all')} className={`review-toggle ${reviewMode === 'all' ? 'active' : ''}`}>{T.showAll}</button>
            </div>

            {visibleReviewItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#16a34a', fontWeight: '700', fontSize: '0.88rem' }}>{T.noMistakes}</div>
            ) : (
              visibleReviewItems.map(({ q, isCorrect, userIdx, opts, qText, explText }) => {
                const lvDef = T.levels[q.cefr];
                return (
                  <div key={q.id} style={{ border: `1.5px solid ${isCorrect ? '#dcfce7' : '#fee2e2'}`, background: isCorrect ? '#f0fdf4' : '#fff1f2', borderRadius: '14px', padding: '16px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ background: lvDef.bg, color: lvDef.color, borderRadius: '6px', padding: '1px 7px', fontSize: '0.66rem', fontWeight: '900' }}>{q.cefr}</span>
                      <span style={{ background: isCorrect ? '#16a34a' : '#dc2626', color: 'white', borderRadius: '6px', padding: '1px 8px', fontSize: '0.66rem', fontWeight: '800' }}>
                        {isCorrect ? `✓ ${T.correctTag}` : `✕ ${T.wrongTag}`}
                      </span>
                    </div>
                    <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.88rem', lineHeight: 1.5, marginBottom: '10px' }}>{qText}</div>
                    {!isCorrect && (
                      <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#be123c', marginBottom: '4px' }}>
                        {T.yourAnswer}: {userIdx != null ? opts[userIdx] : '—'}
                      </div>
                    )}
                    <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#15803d', marginBottom: '8px' }}>
                      {T.correctAnswer}: {opts[q.answer]}
                    </div>
                    <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#64748b', lineHeight: 1.5 }}>💡 {explText}</div>
                  </div>
                );
              })
            )}
          </div>

          {/* History comparison */}
          {history.length > 1 && (
            <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px', border: '1.5px solid #f1f5f9' }}>
              <div style={{ fontWeight: '900', color: '#0f172a', fontSize: '0.9rem', marginBottom: '14px' }}>📈 {T.history}</div>
              {history.slice(0, 4).map((t, i) => {
                const lv2 = T.levels[t.level_result] || T.levels.A1;
                const isThis = i === 0 && saved;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: i < Math.min(history.length, 4) - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <span style={{ background: lv2.bg, color: lv2.color, borderRadius: '8px', padding: '3px 10px', fontSize: '0.7rem', fontWeight: '800' }}>{lv2.label}</span>
                    <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.85rem' }}>{t.score}/{t.total} ({Math.round((t.score / t.total) * 100)}%)</span>
                    {isThis && <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: '50px', padding: '2px 8px', fontSize: '0.65rem', fontWeight: '800' }}>
                      {lang === 'es' ? 'Hoy' : lang === 'en' ? 'Today' : 'Hoje'}
                    </span>}
                    <span style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: '600', marginLeft: 'auto' }}>{new Date(t.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-BR')}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Save button */}
          {!saved ? (
            <button onClick={saveResult} disabled={saving} style={{ width: '100%', padding: '16px', background: saving ? '#f1f5f9' : `linear-gradient(135deg,${lv.color},${lv.color}bb)`, color: saving ? '#94a3b8' : 'white', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '1rem', cursor: saving ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans', boxShadow: saving ? 'none' : `0 4px 16px ${lv.color}40` }}>
              {saving ? T.saving : T.saveResult}
            </button>
          ) : (
            <div style={{ textAlign: 'center', padding: '14px', background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '14px', fontWeight: '800', color: '#15803d' }}>{T.saved}</div>
          )}
          <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '0.72rem', color: '#94a3b8', fontWeight: '600' }}>{T.retakeInfo}</div>
        </div>
      </>
    );
  }

  return null;
}
